import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

// A QR code read with the device's camera, in the console's modal. Built
// for OBS's Connect Info dialog, whose QR code carries the websocket's
// address, port and password (src/lib/obsRemote.js, parseObsws), so nobody
// types a generated password off a screen: the iPad is pointed at the PC.
//
// The frames are decoded with the browser's BarcodeDetector where it has
// one (Chrome on Android, ChromeOS and macOS) and with jsQR elsewhere
// (Safari on the iPad, Chrome on Windows), loaded only when the scanner
// opens. Frames are read at most ~7 times a second and scaled down to 720px
// for jsQR, enough for a QR code filling a third of the picture. The camera
// needs a secure page (https, or localhost) and the site's camera permission.

const SCAN_EVERY_MS = 150;
const DECODE_MAX_WIDTH = 720;

export const cameraAvailable = () =>
  typeof navigator !== 'undefined'
  && !!navigator.mediaDevices?.getUserMedia
  && (typeof window === 'undefined' || window.isSecureContext !== false);

async function makeDecoder() {
  if (typeof BarcodeDetector !== 'undefined') {
    try {
      const formats = await BarcodeDetector.getSupportedFormats?.();
      if (!formats || formats.includes('qr_code')) {
        const detector = new BarcodeDetector({ formats: ['qr_code'] });
        return async (canvas) => {
          const codes = await detector.detect(canvas);
          return codes[0]?.rawValue ?? null;
        };
      }
    } catch {
      // Present but not working here: jsQR, then.
    }
  }
  const mod = await import('jsqr');
  const jsQR = mod.default ?? mod;
  return async (canvas) => {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const { width, height } = canvas;
    const image = ctx.getImageData(0, 0, width, height);
    return jsQR(image.data, width, height, { inversionAttempts: 'dontInvert' })?.data ?? null;
  };
}

function describe(err) {
  switch (err?.name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return 'Camera access was refused. Allow the camera for this site in the browser and try again.';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'No camera was found on this device.';
    case 'NotReadableError':
    case 'AbortError':
      return 'The camera is in use by another app.';
    case 'InsecureContext':
      return 'The camera needs an https page.';
    default:
      return err?.message || 'The camera could not be started.';
  }
}

// `accept(text)` says whether a code read is the one wanted; another is
// shown as `rejectMessage` (a string, or a function of the text read, so the
// message can say what the code held) and scanning goes on. onScan gets the
// text once.
export default function QrScanner({
  open,
  title = 'Scan a QR code',
  hint = null,
  accept = null,
  rejectMessage = 'That is not the QR code wanted.',
  onScan,
  onClose,
}) {
  const videoRef = useRef(null);
  const [status, setStatus] = useState('starting'); // starting | scanning | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    setStatus('starting');
    setMessage('');
    let stopped = false;
    let stream = null;
    let timer = null;
    let rejected = null;
    const canvas = document.createElement('canvas');

    (async () => {
      try {
        if (!cameraAvailable()) throw Object.assign(new Error('insecure'), { name: 'InsecureContext' });
        const decode = await makeDecoder();
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (stopped) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const video = videoRef.current;
        video.srcObject = stream;
        await video.play();
        setStatus('scanning');
        const tick = async () => {
          if (stopped) return;
          try {
            if (video.readyState >= 2 && video.videoWidth > 0) {
              const scale = Math.min(1, DECODE_MAX_WIDTH / video.videoWidth);
              canvas.width = Math.round(video.videoWidth * scale);
              canvas.height = Math.round(video.videoHeight * scale);
              canvas.getContext('2d', { willReadFrequently: true }).drawImage(video, 0, 0, canvas.width, canvas.height);
              const text = await decode(canvas);
              if (text && !stopped) {
                if (accept && !accept(text)) {
                  if (rejected !== text) {
                    rejected = text;
                    setMessage(typeof rejectMessage === 'function' ? rejectMessage(text) : rejectMessage);
                  }
                } else {
                  stopped = true;
                  onScan(text);
                  return;
                }
              }
            }
          } catch {
            // A frame that couldn't be read: the next one.
          }
          timer = setTimeout(tick, SCAN_EVERY_MS);
        };
        tick();
      } catch (err) {
        if (stopped) return;
        setStatus('error');
        setMessage(describe(err));
      }
    })();

    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      stopped = true;
      clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
    // onScan/accept are read when a code is found; a new function identity
    // must not restart the camera.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div className="confirm-modal-backdrop" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-labelledby="qr-scanner-title" className="confirm-modal-layer" onClick={onClose}>
        <div
          className="confirm-modal-panel"
          onClick={(e) => e.stopPropagation()}
          style={{ '--cm-accent': 'var(--neon-blue)', '--cm-glow': 'var(--neon-blue-dim)' }}
        >
          <div className="confirm-modal-inner">
            <div className="confirm-modal-header">
              <h5 id="qr-scanner-title" className="confirm-modal-title">{title}</h5>
              <button type="button" className="confirm-modal-close" aria-label="Close" onClick={onClose}>
                <X size={18} aria-hidden="true" />
              </button>
            </div>
            <div className="confirm-modal-body">
              {hint && <p className="mb-2">{hint}</p>}
              <div className="obs-scan">
                <video ref={videoRef} playsInline muted autoPlay aria-label="Camera" />
                {status === 'scanning' && <div className="obs-scan__frame" aria-hidden="true" />}
                <div className="obs-scan__status" role="status">
                  {status === 'starting' && 'Starting the camera…'}
                  {status === 'scanning' && (message || 'Point the camera at the QR code.')}
                  {status === 'error' && message}
                </div>
              </div>
            </div>
            <div className="confirm-modal-footer">
              <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
